import { useApp } from 'contexts/app';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
} from 'shell/components';

const ViewGradingSettingsDialog = ({ open, onClose }) => {
    const { currentAssignment } = useApp();
    const { t } = useTranslation('assignment');
    const { t: videoT } = useTranslation('videoGrading');

    const handleClose = () => {
        onClose();
    };

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('advanced-grading.title', 'Advanced Grading Settings')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={2}>
                    <Text variant={'p1'} weight={'medium'}>
                        {t(
                            'feedback-settings.additional-instruction.grading-label',
                            'Additional Grading Instructions for the AI'
                        )}
                    </Text>
                    <CustomPaper variant={'outlined'}>
                        <Stack spacing={5}>
                            <Text>
                                {t(
                                    'advanced-grading.description',
                                    'In this section, you can give the AI extra guidance for its grading suggestions. These instructions can be general or tailored to each specific criterion.'
                                )}
                            </Text>
                            <Stack spacing={2}>
                                <Text>{t('advanced-grading.general-instructions', 'General Instructions')}</Text>
                                <Text color={'light'} rich>
                                    {currentAssignment.ai_grading_context || t('common:ui.none', 'None')}
                                </Text>
                            </Stack>
                            <Stack spacing={2}>
                                <Text>
                                    {t('advanced-grading.criterion-instructions', 'Criterion-Specific Instructions')}
                                </Text>
                                <Stack spacing={4}>
                                    {currentAssignment.rubric?.criteria?.map((criterion) => (
                                        <CriterionPaper key={criterion.id} variant={'outlined'}>
                                            <Stack spacing={4}>
                                                <Stack spacing={2}>
                                                    <Text>{criterion.title}</Text>
                                                    <Text color={'light'}>{criterion.description}</Text>
                                                </Stack>
                                                <CriterionInstructionReadOnly
                                                    instruction={
                                                        currentAssignment.ai_grading_settings?.criteria?.[criterion.id]
                                                    }
                                                    t={videoT}
                                                />
                                            </Stack>
                                        </CriterionPaper>
                                    ))}
                                </Stack>
                            </Stack>
                        </Stack>
                    </CustomPaper>
                </Stack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} spacing={4}>
                    <Button aria-hidden={false} color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleClose}>{t('common:controls.close', 'Close')}</Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
};

const CriterionInstructionReadOnly = ({ instruction, t }) => {
    const hasLists = instruction?.look_fors?.length || instruction?.violations_omissions?.length;
    if (!instruction?.grading_instructions && !hasLists) {
        return <Text color={'light'}>{t('empty', 'None')}</Text>;
    }
    return (
        <Stack spacing={3}>
            {instruction?.grading_instructions && (
                <Stack spacing={2}>
                    <Text>{t('criterion.overall-label', 'Overall Criterion Instruction')}</Text>
                    <Text color={'light'} rich>
                        {instruction.grading_instructions}
                    </Text>
                </Stack>
            )}
            {Boolean(instruction?.look_fors?.length) && (
                <Stack spacing={2}>
                    <Text>{t('criterion.look_fors.preview-title', 'Look-fors')}</Text>
                    {instruction.look_fors.map((entry, index) => (
                        <Text key={`${entry}-${index}`} color={'light'}>
                            • {entry}
                        </Text>
                    ))}
                </Stack>
            )}
            {Boolean(instruction?.violations_omissions?.length) && (
                <Stack spacing={2}>
                    <Text>{t('criterion.violations_omissions.preview-title', 'Violations or omissions')}</Text>
                    {instruction.violations_omissions.map((entry, index) => (
                        <Text key={`${entry}-${index}`} color={'light'}>
                            • {entry}
                        </Text>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            maxWidth: '960px',
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        maxWidth: '700px',
        padding: theme.spacing(5),
    };
});

const CriterionPaper = styled(Paper)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

export default ViewGradingSettingsDialog;
